<template>
  <SfModalPortal
    modal-id="reports/create-metric-modal"
    size="s"
    :close-on-backdrop-click="!submitting"
    :close-on-escape="!submitting"
    @opened="resetForm"
    @close="onClose"
  >
    <template #title>{{
      I18n.t('report.drawer.insight.create_modal.title')
    }}</template>

    <template #body>
      <form
        data-testid="create-metric-form"
        class="tw-flex tw-flex-col tw-gap-4"
        @submit.prevent="onSubmit"
      >
        <SfInput
          v-model="title"
          focused
          :label="I18n.t('report.drawer.insight.create_modal.title_label')"
          :placeholder="
            I18n.t('report.drawer.insight.create_modal.title_placeholder')
          "
          :disabled="submitting"
          data-testid="create-metric-title"
        />

        <div
          class="tw-flex tw-flex-col tw-gap-1"
          role="radiogroup"
          :aria-labelledby="graphTypeLabelId"
        >
          <span
            :id="graphTypeLabelId"
            class="tw-text-sm tw-font-bold tw-text-sf-gray-dark"
          >
            {{ I18n.t('report.drawer.insight.create_modal.graph_type_label') }}
          </span>
          <div
            class="tw-grid tw-grid-cols-3 tw-gap-2"
            data-testid="create-metric-graph-type"
          >
            <label
              v-for="option in graphTypeOptions"
              :key="option.value"
              class="
                tw-relative
                tw-flex
                tw-flex-col
                tw-items-center
                tw-gap-1
                tw-rounded-lg
                tw-border
                tw-border-solid
                tw-py-3
                tw-px-2
                tw-font-sans
                tw-m-0
                focus-within:tw-ring-2 focus-within:tw-ring-sf-primary
              "
              :class="cardClassFor(option.value)"
            >
              <input
                v-model="graphType"
                type="radio"
                name="create-metric-graph-type"
                class="tw-sr-only"
                :value="option.value"
                :disabled="submitting"
                :data-testid="`create-metric-graph-type-${option.value}`"
              />
              <span
                v-if="option.beta"
                class="
                  tw-absolute
                  tw-top-1
                  tw-right-1
                  tw-bg-sf-primary
                  tw-text-white
                  tw-text-[10px]
                  tw-font-bold
                  tw-leading-none
                  tw-px-1
                  tw-py-0.5
                  tw-rounded-sm
                "
              >
                {{ I18n.t('js.report_graph.graph_types.beta') }}
              </span>

              <!-- Time series -->
              <svg
                v-if="option.value === 'linechart'"
                width="28"
                height="25"
                viewBox="0 0 28 25"
                fill="none"
                stroke="currentColor"
                stroke-width="2.8"
                stroke-linecap="round"
              >
                <g transform="translate(1 2)">
                  <polyline points="5.32 7.35 12.42 3.15 16.32 7 24.13 2.1" />
                  <line x1="0.71" y1="0" x2="0.71" y2="21" />
                  <line x1="24.84" y1="21" x2="0.71" y2="21" />
                  <line
                    x1="5.32"
                    y1="12.75"
                    x2="5.32"
                    y2="15.95"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                  <line
                    x1="16.68"
                    y1="12.25"
                    x2="16.68"
                    y2="15.75"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                  <line
                    x1="22.35"
                    y1="11.55"
                    x2="22.35"
                    y2="15.75"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                  <line
                    x1="11.00"
                    y1="9.45"
                    x2="11.00"
                    y2="15.95"
                    opacity="0.3"
                    stroke-width="3.5"
                  />
                </g>
              </svg>

              <!--
                Pie: a rounded-rect path drawn with a stroke wider than the
                shape (28.6) is clipped by a mask of itself, leaving only the
                outer ring visible. Two `use`s share that path — the faint
                full ring + a slice via stroke-dasharray.
              -->
              <svg
                v-else-if="option.value === 'piechart'"
                width="29"
                height="28"
                viewBox="0 0 29 28"
                fill="none"
                stroke="currentColor"
              >
                <defs>
                  <rect
                    :id="pieShapeId"
                    x="0"
                    y="0"
                    width="28.06"
                    height="27.3"
                    rx="13.416"
                  />
                  <mask
                    :id="pieMaskId"
                    maskContentUnits="userSpaceOnUse"
                    maskUnits="objectBoundingBox"
                    x="0"
                    y="0"
                    width="28.06"
                    height="27.3"
                    fill="white"
                  >
                    <use :href="`#${pieShapeId}`" />
                  </mask>
                </defs>
                <g fill-rule="nonzero">
                  <use
                    opacity="0.3"
                    :mask="`url(#${pieMaskId})`"
                    stroke-width="28.6"
                    :href="`#${pieShapeId}`"
                  />
                  <use
                    :mask="`url(#${pieMaskId})`"
                    stroke-width="28.6"
                    stroke-dasharray="53.87 47.84"
                    :href="`#${pieShapeId}`"
                  />
                </g>
              </svg>

              <!-- Waterfall -->
              <svg
                v-else-if="option.value === 'waterfallchart'"
                width="29"
                height="28"
                viewBox="0 0 29 28"
                fill="none"
              >
                <g transform="translate(1 2)" stroke="currentColor">
                  <line
                    x1="0.7"
                    y1="0"
                    x2="0.7"
                    y2="23"
                    stroke-width="2.8"
                    stroke-linecap="round"
                  />
                  <line
                    x1="26"
                    y1="23"
                    x2="0.7"
                    y2="23"
                    stroke-width="2.8"
                    stroke-linecap="round"
                  />
                </g>
                <g transform="translate(1 2)" fill="currentColor">
                  <rect x="3" y="7" width="4.5" height="16" rx="0.5" />
                  <rect
                    x="10"
                    y="2"
                    width="4.5"
                    height="5"
                    rx="0.5"
                    opacity="0.5"
                  />
                  <rect
                    x="17"
                    y="7"
                    width="4.5"
                    height="9"
                    rx="0.5"
                    opacity="0.7"
                  />
                  <rect x="24" y="0" width="4.5" height="23" rx="0.5" />
                </g>
              </svg>

              <span class="tw-text-xs">{{ option.label }}</span>
            </label>
          </div>
        </div>

        <SfInput
          v-model="formula"
          :label="I18n.t('report.drawer.insight.create_modal.formula_label')"
          :placeholder="
            I18n.t('report.drawer.insight.create_modal.formula_placeholder')
          "
          :disabled="submitting"
          data-testid="create-metric-formula"
        />

        <p class="tw-text-xs tw-text-sf-neutral-light-1 tw-m-0">
          {{ I18n.t('report.drawer.insight.create_modal.submit_hint') }}
        </p>

        <ul
          v-if="serverErrors.length > 0"
          class="
            tw-list-none
            tw-p-3
            tw-m-0
            tw-rounded-md
            tw-bg-sf-danger-subtle-2
            tw-text-sf-danger-light-2
            tw-text-sm
          "
          data-testid="create-metric-errors"
        >
          <li v-for="(message, index) in serverErrors" :key="index">
            {{ message }}
          </li>
        </ul>

        <!--
          Hidden submit so Enter inside an input reliably triggers the form's
          @submit.prevent. The footer's Create button is outside this form
          (SfModal renders #body and #footer as sibling divs) so it can't
          serve as the implicit-submission target.
        -->
        <button
          type="submit"
          tabindex="-1"
          class="tw-sr-only"
          aria-hidden="true"
        >
          {{ I18n.t('report.drawer.insight.create_modal.submit') }}
        </button>
      </form>
    </template>

    <template #footer>
      <SfButtonDs
        variant="secondary"
        :disabled="submitting"
        data-testid="create-metric-cancel"
        @click="onClose"
      >
        {{ I18n.t('report.drawer.insight.create_modal.cancel') }}
      </SfButtonDs>
      <SfButtonDs
        variant="primary"
        :disabled="!canSubmit"
        data-testid="create-metric-submit"
        @click="onSubmit"
      >
        {{
          submitting
            ? I18n.t('report.drawer.insight.create_modal.submit_in_progress')
            : I18n.t('report.drawer.insight.create_modal.submit')
        }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'

import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { SfInput } from '@app/shared/vue/components/sf-input'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import {
  DEFAULT_GRAPH_TYPE,
  GRAPH_TYPES,
  type GraphType,
} from '@app/application/report/api/drawer'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'CreateMetricModal',
})

const I18n = useI18n()
const store = useReportGraphsDrawer()

// Unique ids for SVG defs/masks (the pie chart's `<use href="#...">` resolves
// against the document, so two mounts of this modal on the same page would
// clash if the ids were hardcoded).
const componentId = useId()
const graphTypeLabelId = `create-metric-graph-type-label-${componentId}`
const pieShapeId = `piechart-shape-${componentId}`
const pieMaskId = `piechart-mask-${componentId}`

const title = ref('')
const formula = ref('')
const graphType = ref<GraphType>(DEFAULT_GRAPH_TYPE)
const submitting = ref(false)
const serverErrors = ref<string[]>([])

// SfInput's update:modelValue is typed `string | undefined`; in practice it
// always emits a string, but the `?? ''` keeps the rest of the code from
// having to defend against the type contract.
const trimmedTitle = computed(() => (title.value ?? '').trim())
const trimmedFormula = computed(() => (formula.value ?? '').trim())

// Reuses the existing graph editor's labels (`js.report_graph.graph_types.*`)
// so the modal stays consistent with the standalone editor at /report_graphs/new.
// `linechart` is presented as "Time series" to match the editor's wording.
const GRAPH_TYPE_LABEL_KEYS: Record<GraphType, string> = {
  linechart: 'js.report_graph.graph_types.timeseries',
  piechart: 'js.report_graph.graph_types.piechart',
  waterfallchart: 'js.report_graph.graph_types.waterfallchart',
}
const GRAPH_TYPES_WITH_BETA: ReadonlySet<GraphType> = new Set(['waterfallchart'])

const graphTypeOptions = computed(() =>
  GRAPH_TYPES.map((value) => ({
    value,
    label: I18n.t(GRAPH_TYPE_LABEL_KEYS[value]),
    beta: GRAPH_TYPES_WITH_BETA.has(value),
  }))
)

function cardClassFor(value: GraphType): string[] {
  const stateClasses =
    graphType.value === value
      ? 'tw-bg-sf-primary-subtle tw-text-sf-primary tw-border-sf-primary'
      : 'tw-bg-sf-snow tw-text-sf-neutral-dark-1 tw-border-sf-snow hover:tw-bg-sf-snow-dark'
  const interactionClasses = submitting.value
    ? 'tw-opacity-50 tw-cursor-not-allowed'
    : 'tw-cursor-pointer'
  return [stateClasses, interactionClasses]
}

const canSubmit = computed(
  () =>
    !submitting.value &&
    trimmedTitle.value.length > 0 &&
    trimmedFormula.value.length > 0
)

function resetForm(): void {
  title.value = ''
  formula.value = ''
  graphType.value = DEFAULT_GRAPH_TYPE
  submitting.value = false
  serverErrors.value = []
}

function onClose(): void {
  if (submitting.value) return
  window.modalCitizen.closeModalPortal()
}

async function onSubmit(): Promise<void> {
  if (!canSubmit.value) return

  submitting.value = true
  serverErrors.value = []

  try {
    await store.createMetric({
      title: trimmedTitle.value,
      formula: trimmedFormula.value,
      graphType: graphType.value,
    })
    window.modalCitizen.closeModalPortal()
  } catch (error) {
    serverErrors.value = collectServerErrors(error)
  } finally {
    submitting.value = false
  }
}

function collectServerErrors(error: unknown): string[] {
  if (typeof error !== 'object' || error === null) return []
  const body = (error as { body?: { errors?: Record<string, string[]> } }).body
  const errors = body?.errors
  if (!errors) return []
  return Object.values(errors).flat().filter((message): message is string =>
    typeof message === 'string' && message.length > 0
  )
}
</script>
